import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const PALETTES = {
  B: { key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838', accent: '#FFD23F', accent2: '#3DC9B5', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5', ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4', mutedText: '#7A6B85', heroStart: '#FFB86B', heroEnd: '#FF5C5C' },
  A: { key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E', accent: '#E8A020', accent2: '#67A844', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844', ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6', mutedText: '#A67C52', heroStart: '#EAF2EA', heroEnd: '#1E5C2E' },
  C: { key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E', accent: '#FFD23F', accent2: '#16A085', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085', ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF', mutedText: '#6A6A80', heroStart: '#B8A9FF', heroEnd: '#5B4EC7' },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';
const Owl = ({ size = 30 }) => <div style={{ fontSize: size * 0.85, lineHeight: 1 }}>🦉</div>;

const PaletteSwitcher = ({ current, onChange }) => (
  <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(255,255,255,0.95)', padding: '8px 12px', borderRadius: 999, border: '2px solid rgba(0,0,0,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontFamily: 'system-ui, sans-serif' }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Màu:</span>
    {Object.values(PALETTES).map(p => {
      const active = current === p.key;
      return <button key={p.key} onClick={() => onChange(p.key)} style={{ width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg, ${p.heroStart}, ${p.heroEnd})`, border: active ? `3px solid ${p.ink}` : '2px solid rgba(0,0,0,0.15)', boxShadow: active ? '0 0 0 2px #fff' : 'none', cursor: 'pointer', padding: 0, transform: active ? 'scale(1.1)' : 'scale(1)' }} />;
    })}
  </div>
);

const PlacementTestIntro = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const handleStart = useCallback(async () => {
    setStarting(true);
    setStartError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/client/placement-test/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setStartError(data?.detail || `HTTP ${res.status}`);
        setStarting(false);
        return;
      }
      sessionStorage.setItem('placement_session', JSON.stringify(data));
      navigate('/placement-test');
    } catch (err) {
      setStartError(err.message);
      setStarting(false);
    }
  }, [navigate]);

  const rules = [
    { icon: '🎯', title: 'Câu dễ → khó', desc: 'Bắt đầu nhẹ nhàng, sau đó Hu sẽ điều chỉnh' },
    { icon: '🧠', title: 'Hu tự điều chỉnh', desc: 'Dựa vào bài làm, Hu chọn câu tiếp theo' },
    { icon: '⏹️', title: 'Dừng bất cứ lúc nào', desc: 'Nhấn "Dừng & xem kết quả" khi nào muốn' },
    { icon: '🎁', title: '+50 Hu Coins', desc: 'Thưởng khi hoàn thành bài kiểm tra' },
  ];

  return (
    <>
      <div style={{ minHeight: '100vh', background: P.cream, fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', background: '#fff', borderBottom: `3px solid ${P.ink}`, flexWrap: 'wrap' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Owl size={30} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>HuPath</div>
          <div style={{ width: 1, height: 22, background: P.ink, opacity: 0.2 }} />
          <div style={{ background: P.accent, color: P.ink, border: `2px solid ${P.ink}`, borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1.5 }}>🎒 HỌC VIÊN MỚI</div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 6 }}>
            {[0, 1, 2, 3].map(i => <div key={i} style={{ width: i < 2 ? 32 : 20, height: 8, borderRadius: 4, background: i < 2 ? P.coral : (i === 2 ? P.plum : P.cream), border: `2px solid ${P.ink}` }} />)}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, opacity: 0.6 }}>Bước 3/4</div>
        </div>

        {/* Body — split 2 cols on desktop */}
        <div className="flex flex-col lg:flex-row" style={{ flex: 1 }}>
          {/* Left: hero */}
          <div style={{
            flex: 1.1, padding: '50px 60px',
            background: `linear-gradient(170deg, ${P.plum} 0%, ${P.ink} 100%)`,
            color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '15%', right: '10%', fontSize: 80, opacity: 0.15 }}>🎯</div>
            <div style={{ position: 'absolute', bottom: '12%', left: '8%', fontSize: 60, opacity: 0.12 }}>📚</div>

            <div style={{ fontSize: 13, fontWeight: 800, color: P.accent, letterSpacing: 3 }}>BƯỚC 3 · PLACEMENT TEST</div>
            <div style={{ fontSize: 48, fontWeight: 800, letterSpacing: -1.5, marginTop: 10, lineHeight: 1.05 }}>
              Bài kiểm tra<br />thông minh 🧠
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 16, lineHeight: 1.55, opacity: 0.9, maxWidth: 440 }}>
              Hu sẽ hỏi từ <b style={{ color: P.accent }}>dễ đến khó</b>. Bé trả lời càng đúng, Hu càng nâng độ khó — cho đến khi tìm đúng trình độ phù hợp.
            </div>

            <div style={{
              marginTop: 30, background: 'rgba(255,255,255,0.1)',
              border: `2px solid rgba(255,255,255,0.3)`, borderRadius: 14,
              padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
              backdropFilter: 'blur(10px)',
            }}>
              <div style={{ fontSize: 32 }}>💡</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: P.accent }}>MẸO NHỎ</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, opacity: 0.9 }}>
                  Không biết cứ đoán — Hu không trừ điểm nhé!
                </div>
              </div>
            </div>
          </div>

          {/* Right: rules */}
          <div style={{ flex: 1, padding: '50px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 600, margin: '0 auto' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: P.ink, marginBottom: 24 }}>Cách thức hoạt động</div>
            {rules.map((r, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 16px', marginBottom: 10,
                background: '#fff', border: `2.5px solid ${P.ink}`, borderRadius: 14,
                boxShadow: `0 4px 0 ${P.ink}`,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: P.cream, border: `2px solid ${P.ink}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22,
                }}>{r.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: P.ink }}>{r.title}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: P.mutedText, marginTop: 2, lineHeight: 1.4 }}>
                    {r.desc}
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button onClick={handleStart} disabled={starting} style={{
                flex: 1, minWidth: 200, padding: '16px', border: `3px solid ${P.ink}`,
                background: starting ? P.sand : P.coral, color: starting ? P.ink : '#fff',
                fontFamily: FONT, fontSize: 16, fontWeight: 800,
                borderRadius: 14, boxShadow: `0 5px 0 ${P.primaryShadow}`, cursor: starting ? 'wait' : 'pointer',
                opacity: starting ? 0.7 : 1,
              }}>{starting ? '⏳ Đang chuẩn bị...' : '🚀 Bắt đầu kiểm tra'}</button>
              {startError && <div style={{ fontSize: 13, color: P.coral, fontWeight: 700, width: '100%' }}>Lỗi: {startError}</div>}
              <button onClick={() => navigate('/dashboard')} style={{
                padding: '16px 20px', border: `3px solid ${P.ink}`,
                background: '#fff', color: P.ink,
                fontFamily: FONT, fontSize: 13, fontWeight: 800,
                borderRadius: 14, boxShadow: `0 5px 0 ${P.ink}`, cursor: 'pointer',
              }}>Để sau</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlacementTestIntro;
